import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, ArrowRight, FolderKanban, ClipboardList, CheckSquare, TrendingUp } from "lucide-react";

function Stat({ label, value, sub, icon: Icon, testId }) {
  return (
    <Card data-testid={testId} className="p-5 border-slate-200 shadow-none">
      <div className="flex items-start justify-between">
        <div className="text-xs font-semibold tracking-widest uppercase text-slate-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-[#F97316]" />}
      </div>
      <div className="mt-3 text-3xl font-bold text-[#0D3B66] tracking-tight">{value}</div>
      {sub && <div className="mt-1.5 text-xs text-slate-500">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "site_engineer") return <SiteEngineerDash />;
  if (user.role === "budget_controller") return <BudgetControllerDash />;
  if (user.role === "admin") return <AdminDash />;
  return <ApproverDash />;
}

function AdminDash() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [sorCount, setSorCount] = useState(0);
  useEffect(() => {
    (async () => {
      try {
        const [p, u, s] = await Promise.all([
          api.get("/projects"),
          api.get("/users"),
          api.get("/sor", { params: { limit: 1000 } }),
        ]);
        setProjects(p.data || []);
        setUsers(u.data || []);
        setSorCount((s.data || []).length);
      } catch { /* ignore */ }
    })();
  }, []);
  const roleCounts = users.reduce((acc, u) => { acc[u.role] = (acc[u.role] || 0) + 1; return acc; }, {});
  return (
    <div className="space-y-8">
      <PageHeader title="Admin Overview" subtitle="Manage users, projects, SOR master and approval thresholds."
        action={<Link to="/users"><Button data-testid="admin-users-btn" className="rounded-full bg-[#0D3B66] hover:bg-[#114B82]">Manage users <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Projects" value={projects.length} testId="stat-admin-projects" icon={FolderKanban} />
        <Stat label="Users" value={users.length} testId="stat-admin-users" />
        <Stat label="SOR Items" value={sorCount} testId="stat-admin-sor" />
        <Stat label="Engineers" value={(roleCounts.site_engineer || 0)} testId="stat-admin-engineers" />
      </div>
      <div>
        <h3 className="text-sm font-semibold tracking-widest uppercase text-slate-500 mb-3">Projects</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} data-testid={`admin-project-card-${p.id}`}>
              <Card className="p-5 border-slate-200 hover:border-[#F97316] hover:shadow-sm transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-base font-semibold text-[#0D3B66]">{p.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.location || "—"}</div>
                  </div>
                  <StatusChip status={p.status} />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function SiteEngineerDash() {
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/indents/mine").then((r) => setIndents(r.data || [])).finally(() => setLoading(false));
  }, []);
  const counts = {
    total: indents.length,
    pending: indents.filter((i) => i.status === "Pending").length,
    approved: indents.filter((i) => i.status === "Approved" || i.status === "Auto-Approved").length,
    rejected: indents.filter((i) => i.status === "Rejected").length,
  };
  return (
    <div className="space-y-8">
      <PageHeader
        title="My Indents"
        subtitle="Raise, track and follow up on your material and service indents."
        action={
          <Link to="/indents/new">
            <Button data-testid="new-indent-btn" className="rounded-full bg-[#F97316] hover:bg-[#EA6A0E] text-white font-semibold">
              <Plus className="h-4 w-4 mr-1.5" /> New Indent
            </Button>
          </Link>
        }
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total" value={counts.total} testId="stat-total" icon={ClipboardList} />
        <Stat label="Pending" value={counts.pending} testId="stat-pending" />
        <Stat label="Approved" value={counts.approved} testId="stat-approved" />
        <Stat label="Rejected" value={counts.rejected} testId="stat-rejected" />
      </div>
      <IndentList items={indents} loading={loading} />
    </div>
  );
}

function BudgetControllerDash() {
  const [projects, setProjects] = useState([]);
  const [summaries, setSummaries] = useState({});
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/projects");
      setProjects(data || []);
      const smap = {};
      for (const p of data || []) {
        try { const { data: s } = await api.get(`/projects/${p.id}/summary`); smap[p.id] = s; } catch {}
      }
      setSummaries(smap);
    })();
  }, []);
  const totalBudget = Object.values(summaries).reduce((a, s) => a + (s?.total_budget || 0), 0);
  const totalApproved = Object.values(summaries).reduce((a, s) => a + (s?.indents_approved_value || 0), 0);
  return (
    <div className="space-y-8">
      <PageHeader title={`Welcome back`} subtitle="Manage project budgets and monitor consumption."
        action={<Link to="/projects"><Button data-testid="view-projects-btn" className="rounded-full bg-[#0D3B66] hover:bg-[#114B82]">View Projects <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="My Projects" value={projects.length} testId="stat-projects" icon={FolderKanban} />
        <Stat label="Total Budget" value={fmtINR(totalBudget)} testId="stat-budget" />
        <Stat label="Approved Indent Value" value={fmtINR(totalApproved)} testId="stat-consumed" icon={TrendingUp} />
      </div>
      <div>
        <h3 className="text-sm font-semibold tracking-widest uppercase text-slate-500 mb-3">Projects</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <ProjectCard key={p.id} p={p} s={summaries[p.id]} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ApproverDash() {
  const [queue, setQueue] = useState({ budgets: [], indents: [] });
  const nav = useNavigate();
  useEffect(() => { api.get("/approvals/queue").then((r) => setQueue(r.data || {budgets:[], indents:[]})); }, []);
  const total = queue.budgets.length + queue.indents.length;
  const totalPendingVal = queue.indents.reduce((a, i) => a + (i.value || 0), 0);
  return (
    <div className="space-y-8">
      <PageHeader title="Approval Queue" subtitle="Budgets and indents awaiting your decision." />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat label="Pending Items" value={total} testId="stat-pending-items" icon={CheckSquare} />
        <Stat label="Budgets" value={queue.budgets.length} testId="stat-pending-budgets" />
        <Stat label="Indents Value" value={fmtINR(totalPendingVal)} testId="stat-pending-value" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold tracking-widest uppercase text-slate-500">Quick actions</h3>
          <Button variant="outline" data-testid="open-approvals-btn" onClick={() => nav("/approvals")} className="rounded-full">Open full queue <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
        </div>
        {total === 0 ? (
          <div data-testid="empty-queue" className="text-center py-10 text-slate-500">Nothing pending. You're all caught up.</div>
        ) : (
          <div className="text-sm text-slate-600">
            {queue.budgets.length} budget(s) and {queue.indents.length} indent(s) waiting.
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectCard({ p, s }) {
  const pct = s?.consumption_pct || 0;
  return (
    <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`} className="block">
      <Card className="p-5 border-slate-200 hover:border-[#F97316] hover:shadow-sm transition-all">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-[#0D3B66]">{p.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{p.location || "—"}</div>
          </div>
          <StatusChip status={p.status} />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-slate-500 uppercase tracking-wider">Budget</div>
            <div className="text-slate-800 font-semibold mt-0.5">{fmtINR(s?.total_budget)}</div>
          </div>
          <div>
            <div className="text-slate-500 uppercase tracking-wider">Consumed</div>
            <div className="text-slate-800 font-semibold mt-0.5">{pct ? pct.toFixed(1) : 0}%</div>
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#F97316] to-[#FBBF24]" style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
      </Card>
    </Link>
  );
}

function IndentList({ items, loading }) {
  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (items.length === 0) return <div data-testid="empty-indents" className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-500">No indents yet. Click "New Indent" to raise one.</div>;
  return (
    <div className="space-y-3">
      {items.map((i) => (
        <Link key={i.id} to={`/indents`} data-testid={`indent-row-${i.id}`}>
          <Card className="p-4 border-slate-200 hover:border-[#F97316] transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{i.iow_code}</span>
                  <span className="text-sm font-semibold text-slate-800 truncate">{i.description}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1.5">Qty {i.qty} {i.uom} · {fmtDate(i.raised_at)}</div>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <div className="text-sm font-semibold text-[#0D3B66]">{fmtINR(i.value)}</div>
                <StatusChip status={i.status} />
              </div>
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1.5 text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
